import { useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LayoutContainer from 'components/Common/LayoutContainer'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Divider from 'LuxKit/Divider'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ContactContext from 'contexts/contactContext'
import type { OfferPageMultiRoomAutoselectionReason, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import { EmptyArray } from 'lib/array/arrayUtils'
import {
  arePropertyRatesLoading,
  getCurrentAvailableRates,
  getPropertyLowestAvailableStayPayTier,
} from 'selectors/offerPage/offerPageSelectors'
import { getPropertyIndividualRowsByRoomId } from 'selectors/offerPage/propertyMultipleRoomBookingSelectors'
import type { RoomRateViews } from './singlePropertyUtils'

import PropertyIndividualRoomRoom from './PropertyIndividualRoomRoom'
import PreSelectionBanner from '../MultipleRoomsBooking/PreSelectionBanner'
import InvalidSelectionBanner from '../MultipleRoomsBooking/InvalidSelectionBanner'
import PropertyMultiIndividualRoomsFooter from './PropertyMultiIndividualRoomsFooter'

const GreyGroup = styled(Group)`
  background-color: ${props => props.theme.palette.neutral.default.six};

  ${mediaQueryUp.tablet} {
    background-color: ${props => props.theme.palette.neutral.default.eight};
  }
`

interface Props {
  onViewPackageOptions: () => void
  property: App.Property.Property
  saleUnit: string
  rooms: Array<OfferPageRoom>
  roomOptionsByRoomId: Record<string, Array<RoomRateViews>>
  hasRateError: boolean
  checkIn?: string
  lowestAvailableStayPayTier?: App.StayPayTier | null
  ratesLoading: boolean
  autoselectionReason?: OfferPageMultiRoomAutoselectionReason
}

function PropertyDesktopIndividualRooms(props: Props) {
  const {
    onViewPackageOptions,
    property,
    saleUnit,
    rooms = EmptyArray,
    roomOptionsByRoomId,
    hasRateError,
    checkIn,
    lowestAvailableStayPayTier,
    ratesLoading,
    autoselectionReason,
  } = props

  const intl = useIntl()
  const supportContact = useContext(ContactContext)

  const roomsAreSelected = rooms.some(room => room.roomId)
  let selectionBanner: React.ReactNode = null
  if (roomsAreSelected) {
    if (autoselectionReason === 'initial-selection') {
      selectionBanner = <PreSelectionBanner saleUnit={saleUnit} />
    } else if (autoselectionReason === 'invalid-selections') {
      selectionBanner = <InvalidSelectionBanner saleUnit={saleUnit} />
    }
  }

  return (
    <Pane>
      <VerticalSpacer gap={16}>
        {(hasRateError || (!checkIn && lowestAvailableStayPayTier)) && <LayoutContainer>
          {hasRateError && <MessageBanner
            kind="warning"
            tabletAlign="center"
            heading={intl.formatMessage(
              {
                id: 'BnvLw0OFjcVH9KIHccT2',
                defaultMessage: 'The third party booking system for {propertyName} is currently down',
                description: 'Error message heading when property booking system is down',
              },
              { propertyName: property.name },
            )}
          >
            {intl.formatMessage(
              {
                id: 'tGbUfKIAF_biyC8JvkSw',
                defaultMessage: 'Please call our VIP team on {phoneNumber} who are ready to provide assistance to complete your booking',
                description: 'Error message body with phone number when property booking system is down',
              },
              { phoneNumber: supportContact.local.humanReadable },
            )}
          </MessageBanner>}
          {!checkIn && lowestAvailableStayPayTier && <MessageBanner
            kind="promotional"
            icon={<LineInfoCircleIcon />}
          >
            <FormattedMessage
              id="w_UoFwnNZR6UH1nCXBD5"
              defaultMessage="<b>Special offer:</b> Enjoy free nights for stays longer than {dayCountLabel}. Select dates to check eligibility."
              description="Stay pay tier promotional banner message in the loyalty section"
              values={{
                dayCountLabel: intl.formatMessage(
                  { id: '--kBF6UkfAMNYHooUklh', defaultMessage: '{count, plural, one {# day} other {# days}}', description: 'Stay length day count label shown in special offer banner' },
                  { count: lowestAvailableStayPayTier.stayNights },
                ),
                b: (chunks) => <b>{chunks}</b>,
              }}
            />
          </MessageBanner>}
        </LayoutContainer>}
        {!hasRateError && (
          <>
            <CSSBreakpoint max="mobile" dynamic>
              <Divider kind="primary" />
            </CSSBreakpoint>
            <GreyGroup direction="vertical" gap={8} tabletGap={24}>
              {rooms.map((room, index) => {
                const roomOptions = roomOptionsByRoomId[room.id] ?? EmptyArray
                let roomContent: React.ReactNode
                if (ratesLoading) {
                  roomContent = (
                    <LayoutContainer key={room.id}>
                      <LoadingIndicator inline />
                    </LayoutContainer>
                  )
                } else {
                  roomContent = (
                    <PropertyIndividualRoomRoom
                      key={room.id}
                      occupancyRoom={room}
                      roomOptions={roomOptions}
                      onViewPackageOptions={onViewPackageOptions}
                      packageNumber={index + 1}
                    />
                  )
                }
                if (index === 0 && selectionBanner) {
                  return <div key={room.id}>
                    {selectionBanner}
                    {roomContent}
                  </div>
                }
                return roomContent
              })}
              <PropertyMultiIndividualRoomsFooter />
            </GreyGroup>
          </>
        )}
      </VerticalSpacer>
    </Pane>
  )
}

type MappedProps = Omit<Props, 'onViewPackageOptions'> & { open?: boolean }

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  property: state.property!,
  saleUnit: 'room',
  rooms: state.rooms,
  roomOptionsByRoomId: getPropertyIndividualRowsByRoomId(state, appState),
  hasRateError: !!getCurrentAvailableRates(state, appState)?.error,
  checkIn: state.checkInDate,
  lowestAvailableStayPayTier: getPropertyLowestAvailableStayPayTier(state, appState),
  ratesLoading: arePropertyRatesLoading(state, appState),
  open: !!state.activeFilter,
  autoselectionReason: state.multiRoomAutoselectionReason,
}), PropertyDesktopIndividualRooms, preventRenderWithOpenFilter)
